jest.mock('routes', () => ({}), { virtual: true });
jest.mock('utils/fetch', () => ({ customFetch: jest.fn() }));

import FractionsStore from './FractionsStore';
import { expansionStorageKey } from 'productionOrders/utils/fractionsExpansion';

// PLA-860 v3 (D1, D4): estado abierto/cerrado de las fracciones y de los grupos de subdivisiones.
const fraction = (id: number, node: string, weight: number, extra = {}) => ({
  id: String(id),
  type: 'fraction',
  attributes: {
    node_identifier: node,
    depth: node.split('-').length - 1,
    weight: String(weight),
    rejected: 0,
    remaining: weight,
    milling_order_id: 1,
    fraction_reprocesses: [],
    ...extra,
  },
});

const subfractions = (...ids: number[]) => ({
  relationships: {
    subfractions: { data: ids.map((id) => ({ id: String(id), type: 'fraction' })) },
  },
});

// 1 (dividida en 11 y 12; 11 dividida en 111 y 112, 111 rechazada) y 2 (sin dividir).
const payload = {
  data: [{ ...fraction(1, '1', 1400), ...subfractions(11, 12) }, fraction(2, '2', 1219)],
  included: [
    {
      ...fraction(11, '1-1', 700, { split_method: 'by_count' }),
      ...subfractions(111, 112),
    },
    fraction(12, '1-2', 700, { evaluation_result: 'approved' }),
    fraction(111, '1-1-1', 350, { evaluation_result: 'rejected' }),
    fraction(112, '1-1-2', 350),
  ],
};

const buildStore = (collapsed: boolean, storageKey?: string) =>
  new FractionsStore(
    1000,
    '2619',
    payload as never,
    { data: [] } as never,
    { pppt: { min: 0, max: 0 } },
    [],
    [],
    70,
    'contract_packaging',
    { collapsed, storageKey }
  );

const byNode = (store: FractionsStore, node: string) =>
  store.expandableFractions.find((item) => item.node_identifier === node);

describe('FractionsStore: expansion', () => {
  let storage: Record<string, string>;

  beforeEach(() => {
    storage = {};
    (global as { window?: unknown }).window = {
      localStorage: {
        getItem: (key: string) => (key in storage ? storage[key] : null),
        setItem: (key: string, value: string) => {
          storage[key] = value;
        },
      },
    };
  });

  afterEach(() => {
    delete (global as { window?: unknown }).window;
  });

  it('solo cuenta las filas con hijas, en cualquier nivel', () => {
    const store = buildStore(false);

    expect(store.expandableFractions.map((item) => item.node_identifier)).toEqual([
      '1',
      '1-1',
    ]);
  });

  it('en "Finalizar" arranca todo abierto', () => {
    const store = buildStore(false);

    expect(store.allExpanded).toBe(true);
  });

  it('en "Finalizar QA" arranca todo cerrado', () => {
    const store = buildStore(true);

    expect(byNode(store, '1')?.expanded).toBe(false);
    expect(byNode(store, '1-1')?.expanded).toBe(false);
  });

  it('contrae un grupo y lo recuerda en el navegador', () => {
    const key = expansionStorageKey(1, 5, 'finalise');
    const store = buildStore(false, key);

    byNode(store, '1-1')?.toggleExpanded();

    expect(byNode(store, '1-1')?.expanded).toBe(false);
    expect(byNode(buildStore(false, key), '1-1')?.expanded).toBe(false);
    expect(byNode(buildStore(false, key), '1')?.expanded).toBe(true);
  });

  it('la etapa "Finalizar QA" no hereda lo abierto en "Finalizar"', () => {
    buildStore(false, expansionStorageKey(1, 5, 'finalise')).setAllExpanded(true);

    const store = buildStore(true, expansionStorageKey(1, 5, 'qa'));

    expect(store.expandedIds.size).toBe(0);
  });

  it('expande y contrae todo', () => {
    const store = buildStore(true);

    store.setAllExpanded(true);
    expect(store.allExpanded).toBe(true);

    store.setAllExpanded(false);
    expect(store.expandedIds.size).toBe(0);
  });

  it('sin almacenamiento disponible usa el estado inicial', () => {
    (global as { window?: unknown }).window = {
      localStorage: {
        getItem: () => {
          throw new Error('blocked');
        },
        setItem: () => {
          throw new Error('blocked');
        },
      },
    };

    const store = buildStore(true, expansionStorageKey(1, 5, 'qa'));
    store.setAllExpanded(true);

    expect(store.allExpanded).toBe(true);
  });

  it('resume las hojas de un grupo contraido', () => {
    const store = buildStore(true);

    expect(byNode(store, '1')?.subdivisionSummary).toEqual({
      count: 3,
      rejectedCount: 1,
      rejectedWeight: 350,
    });
    expect(byNode(store, '1-1')?.subdivisionSummary).toEqual({
      count: 2,
      rejectedCount: 1,
      rejectedWeight: 350,
    });
  });
});
